import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useIdleLogout } from "../hooks/useIdleLogout";

const AuthContext = createContext(null);
const PENDING_2FA_KEY = "hris_pending_totp";

function readPendingTwoFactor() {
  try {
    const saved = sessionStorage.getItem(PENDING_2FA_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    if (!parsed?.challengeId) return null;
    return {
      challengeId: parsed.challengeId,
      setupRequired: Boolean(parsed.setupRequired),
      expiresAt: parsed.expiresAt,
      qrCodeDataUri: null,
      manualSetupKey: null,
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  const [loading, setLoading] = useState(true);
  const [pendingTwoFactor, setPendingTwoFactor] = useState(readPendingTwoFactor);

  const savePendingTwoFactor = (data, current = null) => {
    const pending = {
      challengeId: data.challenge_id || current?.challengeId,
      setupRequired: Boolean(data.setup_required ?? current?.setupRequired),
      expiresAt: data.expires_in != null
        ? Date.now() + data.expires_in * 1000
        : current?.expiresAt,
      qrCodeDataUri: data.qr_code_data_uri || current?.qrCodeDataUri || null,
      manualSetupKey: data.manual_setup_key || current?.manualSetupKey || null,
    };
    try {
      sessionStorage.setItem(PENDING_2FA_KEY, JSON.stringify({
        challengeId: pending.challengeId,
        setupRequired: pending.setupRequired,
        expiresAt: pending.expiresAt,
      }));
    } catch {
      /* State still works when browser storage is unavailable. */
    }
    setPendingTwoFactor(pending);
    return pending;
  };

  const clearPendingTwoFactor = () => {
    try {
      sessionStorage.removeItem(PENDING_2FA_KEY);
      sessionStorage.removeItem("hris_pending_2fa");
    } catch {
      /* Ignore unavailable browser storage. */
    }
    setPendingTwoFactor(null);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.requires_2fa) {
      setUser(false);
      savePendingTwoFactor(data);
      return data;
    }
    clearPendingTwoFactor();
    setUser(data);
    return data;
  };

  const verifyTwoFactor = async (code) => {
    if (!pendingTwoFactor?.challengeId) {
      throw new Error("Sesi verifikasi 2FA tidak ditemukan");
    }
    const { data } = await api.post("/auth/2fa/verify", {
      challenge_id: pendingTwoFactor.challengeId,
      code,
    });
    clearPendingTwoFactor();
    setUser(data);
    return data;
  };

  const loadTwoFactorSetup = async () => {
    if (!pendingTwoFactor?.challengeId) {
      throw new Error("Sesi setup authenticator tidak ditemukan");
    }
    const { data } = await api.post("/auth/2fa/setup", {
      challenge_id: pendingTwoFactor.challengeId,
    });
    return savePendingTwoFactor(data, pendingTwoFactor);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore network errors on logout */
    }
    clearPendingTwoFactor();
    setUser(false);
  };

  useIdleLogout({
    authenticated: Boolean(user),
    area: "admin",
    heartbeat: () => api.post("/auth/activity"),
    onLogout: logout,
    loginPath: "/login",
  });

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      setUser,
      pendingTwoFactor,
      verifyTwoFactor,
      loadTwoFactorSetup,
      clearPendingTwoFactor,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
